import { Controller } from "react-hook-form";
import { TextInput, Select, TextArea, FormSection } from "../../../../_components/ui";
import CompanySelect from "../../../../_components/form/CompanySelect";

const TYPE_OPTIONS = [
  { label: "Upsell", value: "Upsell" },
  { label: "Downsell", value: "Downsell" },
];

const ADDRESS_FIELDS = [
  { key: "addr1", label: "Address line 1" },
  { key: "addr2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "zipCode", label: "Pin code" },
];

/**
 * Read-only display of one of the selected company's addresses - unlike
 * Proposals' AddressFieldGroup, the old wizard never submitted billing/
 * shipping address as part of the upsell payload at all (grepped the old
 * handleCreateUpsell - no `billing`/`shipping` key anywhere in it), it was
 * purely a confirmation display mirrored straight off `company.address`/
 * `company.secondaryAddress`. So this stays a plain presentational block,
 * not RHF-registered fields, rather than force-fitting AddressFieldGroup's
 * Controller-based contract onto values nothing ever submits.
 */
const AddressDisplay = ({ title, address }) => (
  <div className="upsell-customer-details-step__addresses">
    <h4 className="upsell-customer-details-step__section-title">{title}</h4>
    <div className="upsell-customer-details-step__address-fields">
      {ADDRESS_FIELDS.map((field) => (
        <TextInput
          key={field.key}
          label={field.label}
          value={address?.[field.key] || ""}
          onChange={() => {}}
          disabled
        />
      ))}
    </div>
  </div>
);

/**
 * Step 1 - upsell/downsell title, type, description, company, and its
 * (read-only, mirrored) billing/shipping addresses. Mirrors
 * _features/proposals' CustomerDetailsStep's shape.
 *
 * `CompanySelect` now lives in `_components/form/CompanySelect` (moved as a
 * pure relocation, not rewritten - see docs/bug-sheet.md).
 * `TextEditor` has since moved to `_components/ui/TextEditor`.
 *
 * This step used to also collect a "deal contacts" list, separate from step
 * 3's "Send upsell to" recipients - removed at the user's request, since it
 * duplicated a concept the wizard already has a single, correctly-modeled
 * home for (`sendTo`). See useUpsellWizard.js's header comment for the full
 * history.
 */
const UpsellCustomerDetailsStep = ({ control, watch, isPreview, lockCompany }) => {
  const company = watch("company");
  const type = watch("type") || "Upsell";

  const handleCompanyChange = (onFieldChange) => (selectedCompany) => {
    onFieldChange(selectedCompany);
  };

  return (
    <div className="upsell-customer-details-step">
      <FormSection title="Upsell details">
        <Controller
          name="title"
          control={control}
          rules={{
            required: "Title is required.",
            validate: (value) => !!String(value ?? "").trim() || "Title is required.",
          }}
          render={({ field, fieldState }) => (
            <TextInput
              id="title"
              label={`${type} title`}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              disabled={isPreview}
            />
          )}
        />

        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select
              id="type"
              label="Select Upsell or Downsell"
              options={TYPE_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              disabled={isPreview}
            />
          )}
        />

        <Controller
          name="company"
          control={control}
          rules={{ required: "Company is required." }}
          render={({ field, fieldState }) => (
            <CompanySelect
              label="Company"
              value={field.value}
              onChange={handleCompanyChange(field.onChange)}
              context="upsell"
              error={fieldState.error?.message}
              disabled={lockCompany || isPreview}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextArea
              id="description"
              label="Description"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isPreview}
            />
          )}
        />
      </FormSection>

      <FormSection>
        <AddressDisplay title="Billing address" address={company?.address} />
        <AddressDisplay title="Shipping address" address={company?.secondaryAddress} />
      </FormSection>
    </div>
  );
};

export default UpsellCustomerDetailsStep;
